import queryString from 'query-string'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { matchPath } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import BoxAndLinks from 'src/components/BoxAndLinks/BoxAndLinks'
import { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBox, {
  CheckboxProps,
} from 'src/components/VirtualCardsBox/VirtualCardsBox'
import {
  VirtualCardFilterStatus,
  VirtualCardTypes,
} from 'src/enums'
import {
  useDebounced,
  useQueryParamArrayHandler,
  useSortable,
  useStringSetState,
} from 'src/hooks'
import {
  VirtualCardListModel,
  VirtualCardStatus,
} from 'src/models/virtualCard.models'
import {
  accountsUrl,
  budgetsUrl,
  yourCardsActiveUrl,
  yourCardsInactiveUrl,
} from 'src/navigation/urls'
import {
  selectCalculatedBackgroundLocation,
  selectIsLeftFlyoutOpen,
  selectIsRightFlyoutOpen,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { InfiniteScrollProps } from 'src/types'

export interface VirtualCardsFilters {
  keyword: string
  recipients: string[]
  sortBy?: string
  sortDescending?: boolean
  statuses?: VirtualCardStatus[]
  cardTypes?: VirtualCardTypes[]
  deactivatingIn?: string
}

export interface VirtualCardsFiltersBody {
  search?: string
  statuses?: VirtualCardStatus[]
  recipients?: string[]
  cardholders?: string[]
  cardTypes?: VirtualCardTypes[]
  deactivatingIn?: string
  sortField?: string
  sortDirection?: string
  issued?: boolean
  isDirect?: boolean
  isRecurring?: boolean
  creditCardId?: string
  creditCardIds?: string[]
}

export interface VirtualCardsAnalyticsEventProperties {
  card_user?: string[]
  status?: string[]
  search?: string
  card_type?: string[]
  deactivating_in?: string
}

type VirtualCardsBoxAndLinksProps = {
  onDownloadClick?: (filters: VirtualCardsFiltersBody) => void
  defaultTitle?: React.ReactElement
  virtualCards: VirtualCardListModel[]
  navAway?: NavAwayProps
  emptyStateDesign: TableEmptyStateDesigns
  isYourCards?: boolean
  hideFilters?: boolean
  infiniteScrollProps: InfiniteScrollProps<VirtualCardsFiltersBody>
  initialFilterValues?: Partial<VirtualCardsFilters>
  usersFullPaginatedData: UsersFullPaginatedDataState
  totalCount: number
  readOnly?: boolean
  checkboxProps?: CheckboxProps
  setSelectedFilters?: (filters: VirtualCardsAnalyticsEventProperties) => void
  clientHeight?: number
}

const VirtualCardsBoxAndLinks: React.FC<VirtualCardsBoxAndLinksProps> = (
  props,
): React.ReactElement => {
  const { analytics } = useAnalyticsContext()
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const queryParams = useSelector(selectQueryParams)
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)

  const handlePageLocation = (): string => {
    return matchPath(calculatedBackgroundLocation.pathname, accountsUrl())
      ? 'accounts'
      : matchPath(calculatedBackgroundLocation.pathname, budgetsUrl())
        ? 'budgets'
        : matchPath(calculatedBackgroundLocation.pathname, yourCardsActiveUrl())
          ? 'active and pending'
          : matchPath(
                calculatedBackgroundLocation.pathname,
                yourCardsInactiveUrl(),
              )
            ? 'inactive'
            : ''
  }

  const initialFilterValues: VirtualCardsFilters = useMemo(
    () => ({
      keyword: '',
      recipients: [],
      deactivatingIn: '',
      sortBy: 'updatedAt',
      sortDescending: true,
      ...props.initialFilterValues,
    }),
    [props.initialFilterValues],
  )

  const calculatedStatusFilter = (statuses: string[]): string[] => {
    const statusFilter = []

    // assumes query params will come in the expected combinations
    if (statuses?.some((status) => status === VirtualCardStatus.Active)) {
      statusFilter.push(VirtualCardFilterStatus.active)
    }
    if (statuses?.some((status) => status === VirtualCardStatus.Pending)) {
      statusFilter.push(VirtualCardFilterStatus.pending)
    }
    if (
      statuses?.some((status) => status === VirtualCardStatus.Cancelled) &&
      statuses?.some((status) => status === VirtualCardStatus.Expired) &&
      statuses?.some((status) => status === VirtualCardStatus.Rejected)
    ) {
      statusFilter.push(VirtualCardFilterStatus.inactive)
    }
    if (
      statuses?.some((status) => status === VirtualCardStatus.Closed) &&
      statuses?.some((status) => status === VirtualCardStatus.Consumed)
    ) {
      statusFilter.push(VirtualCardFilterStatus.closed)
    }
    return statusFilter
  }

  const [keywordFilter, setKeywordFilter] = useState(
    initialFilterValues.keyword,
  )
  const {
    set: statusesFilter,
    setSet: setStatusesFilter,
    handleSelection: handleStatusSelection,
  } = useStringSetState(
    new Set<string>(initialFilterValues.statuses) ?? new Set<string>(),
  )
  const {
    set: recipientFilter,
    setSet: setRecipientFilter,
    handleSelection: handleRecipientSelection,
  } = useStringSetState(
    new Set<string>(initialFilterValues.recipients) ?? new Set<string>(),
  )
  const {
    set: cardTypesFilter,
    setSet: setCardTypesFilter,
    handleSelection: handleCardTypesSelection,
  } = useStringSetState(
    new Set<string>(initialFilterValues.cardTypes) ?? new Set<string>(),
  )
  const { setSort, sortBy, sortDescending } = useSortable(
    initialFilterValues.sortBy,
    initialFilterValues.sortDescending,
  )
  const [deactivatingInFilter, setDeactivatingInFilter] = useState(
    initialFilterValues.deactivatingIn,
  )

  const calculatedStatuses = (
    statusesFilter: Set<string>,
  ): VirtualCardStatus[] => {
    const filteredStatuses = []
    if (statusesFilter.size > 0) {
      if (
        statusesFilter.has(VirtualCardFilterStatus.active) ||
        statusesFilter.has(VirtualCardStatus.Active)
      ) {
        filteredStatuses.push(VirtualCardStatus.Active)
      }
      if (
        statusesFilter.has(VirtualCardFilterStatus.pending) ||
        statusesFilter.has(VirtualCardStatus.Pending)
      ) {
        filteredStatuses.push(VirtualCardStatus.Pending)
      }
      if (
        statusesFilter.has(VirtualCardFilterStatus.inactive) ||
        statusesFilter.has(VirtualCardStatus.Cancelled) ||
        statusesFilter.has(VirtualCardStatus.Expired) ||
        statusesFilter.has(VirtualCardStatus.Rejected)
      ) {
        filteredStatuses.push(
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Expired,
          VirtualCardStatus.Rejected,
        )
      }
      if (
        statusesFilter.has(VirtualCardFilterStatus.closed) ||
        statusesFilter.has(VirtualCardStatus.Closed) ||
        statusesFilter.has(VirtualCardStatus.Consumed)
      ) {
        filteredStatuses.push(
          VirtualCardStatus.Closed,
          VirtualCardStatus.Consumed,
        )
      }
    }
    return filteredStatuses
  }

  const filtersBody: VirtualCardsFiltersBody = {
    search: keywordFilter ? `${keywordFilter}` : undefined,
    statuses: statusesFilter
      ? Array.from(calculatedStatuses(statusesFilter))
      : undefined,
    recipients: recipientFilter ? Array.from(recipientFilter) : undefined,
    cardTypes: cardTypesFilter
      ? (Array.from(cardTypesFilter) as VirtualCardTypes[])
      : undefined,
    deactivatingIn: deactivatingInFilter
      ? `${deactivatingInFilter}`
      : undefined,
    sortField: sortBy,
    sortDirection: sortDescending === false ? 'ASC' : 'DESC',
  }

  const paramString = queryString.stringify({
    search: filtersBody.search,
    recipients: filtersBody.recipients,
    statuses: filtersBody.statuses,
    cardTypes: filtersBody.cardTypes,
    deactivatingIn: filtersBody.deactivatingIn,
  })

  const pushUrlState = (): void => {
    window.history.pushState(
      JSON.stringify(null),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  const replaceUrlState = (): void => {
    window.history.replaceState(
      JSON.stringify({}),
      '',
      paramString.length > 0 ? `?${paramString}` : location.pathname,
    )
  }

  // restores query params on flyout close
  useEffect((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      replaceUrlState()
    }
  }, [isRightFlyoutOpen, isLeftFlyoutOpen])

  useEffect((): void => {
    if (pushNewSearchState) {
      pushUrlState()
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  const { debouncedF } = useDebounced((): void => {
    if (!isRightFlyoutOpen && !isLeftFlyoutOpen) {
      props.infiniteScrollProps.fetch(filtersBody, 0)
    }
  })

  useEffect((): void => {
    debouncedF()
    props.setSelectedFilters !== undefined &&
      props.setSelectedFilters({
        card_user:
          recipientFilter.size > 0 ? Array.from(recipientFilter) : undefined,
        status:
          statusesFilter.size > 0 ? Array.from(statusesFilter) : undefined,
        search: keywordFilter,
        card_type:
          cardTypesFilter.size > 0 ? Array.from(cardTypesFilter) : undefined,
        deactivating_in: deactivatingInFilter,
      })
  }, [
    keywordFilter,
    statusesFilter,
    recipientFilter,
    cardTypesFilter,
    deactivatingInFilter,
    sortBy,
    sortDescending,
  ])

  useEffect((): void => {
    setRecipientFilter(
      new Set(useQueryParamArrayHandler(queryParams.recipients)),
    )
    setKeywordFilter(queryParams.search ? queryParams.search : '')
    setStatusesFilter(
      new Set(
        calculatedStatusFilter(
          useQueryParamArrayHandler(queryParams.statuses) as string[],
        ),
      ),
    )
    setCardTypesFilter(
      new Set(useQueryParamArrayHandler(queryParams.cardTypes)),
    )
    setDeactivatingInFilter(
      queryParams.deactivatingIn ? queryParams.deactivatingIn : '',
    )
  }, [queryParams])

  useEffect((): void => {
    if (keywordFilter !== '') {
      analytics?.track(
        handlePageLocation() + ' virtual cards filter - search cards - typing',
        {
          intent: 'filter cards',
          screen: calculatedBackgroundLocation.pathname,
          interaction: 'typing',
          results: props.infiniteScrollProps.totalItems || 0,
          search: keywordFilter,
        },
      )
    }
  }, [props.infiniteScrollProps.totalItems || keywordFilter])

  return (
    <BoxAndLinks>
      <VirtualCardsBox
        {...props}
        loading={props.infiniteScrollProps.loading}
        complete={props.infiniteScrollProps.complete}
        readOnly={!!props.readOnly}
        fullProps={{
          keywordFilter: keywordFilter,
          setKeywordFilter: setKeywordFilter,
          statusesFilter: Array.from(statusesFilter),
          setStatusesFilter: setStatusesFilter,
          handleStatusSelection: handleStatusSelection,
          recipientFilter: Array.from(recipientFilter),
          setRecipientFilter: setRecipientFilter,
          handleRecipientSelection: handleRecipientSelection,
          setPushNewSearchState: setPushNewSearchState,
          cardTypesFilter: Array.from(cardTypesFilter),
          setCardTypesFilter: setCardTypesFilter,
          handleCardTypesSelection: handleCardTypesSelection,
          deactivatingInFilter: deactivatingInFilter,
          setDeactivatingInFilter: setDeactivatingInFilter,
          sortableProps: {
            setSort,
            sortBy,
            sortDescending: sortDescending,
          },
          filteredCount: props.infiniteScrollProps.totalItems,
        }}
        isYourCards={props.isYourCards}
        hideFilters={props.hideFilters}
        emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
        usersFullPaginatedData={props.usersFullPaginatedData}
        checkboxProps={props.checkboxProps}
        infiniteScrollProps={props.infiniteScrollProps}
        filtersBody={filtersBody}
        clientHeight={props.clientHeight}
      />
    </BoxAndLinks>
  )
}

export default VirtualCardsBoxAndLinks
